import { useEffect } from 'react';
import type {
  CampaignBuilderMode,
  CreativePreview,
  ManualCampaignForm,
  PreviewData,
  SelectedScreenMeta,
  StepThreeData,
  StepTwoData,
} from '@/features/campaigns/types/campaign';
import {
  clearStandardCampaignDraft,
  saveStandardCampaignDraft,
} from '@/features/campaigns/utils/standardCampaignDraftStorage';

type StandardDraft = {
  currentStep: number;
  campaignData: ManualCampaignForm;
  stepTwoData: StepTwoData;
  selectedScreensMeta: Record<string, SelectedScreenMeta>;
  submitData: StepThreeData;
  previewData: PreviewData;
  creativePreview: CreativePreview;
  skipScreenStep?: boolean;
};

type UseStandardDraftPersistenceParams = {
  builderMode: CampaignBuilderMode | null;
  isCampaignCompleted: boolean;
  showSuccess: boolean;
  draft: StandardDraft;
};

/** Persists manual create step + data so a refresh stays on the same step. */
export function useStandardDraftPersistence({
  builderMode,
  isCampaignCompleted,
  showSuccess,
  draft,
}: UseStandardDraftPersistenceParams) {
  const {
    currentStep,
    campaignData,
    stepTwoData,
    selectedScreensMeta,
    submitData,
    previewData,
    creativePreview,
    skipScreenStep,
  } = draft;

  useEffect(() => {
    if (builderMode !== 'standard') return;
    if (isCampaignCompleted || showSuccess) {
      clearStandardCampaignDraft();
      return;
    }
    saveStandardCampaignDraft({
      currentStep,
      campaignData,
      stepTwoData,
      selectedScreensMeta,
      submitData,
      previewData,
      creativePreview,
      skipScreenStep: Boolean(skipScreenStep),
    });
  }, [
    builderMode,
    currentStep,
    campaignData,
    stepTwoData,
    selectedScreensMeta,
    submitData,
    previewData,
    creativePreview,
    skipScreenStep,
    isCampaignCompleted,
    showSuccess,
  ]);
}
